/**
 * Geometry for landing the canvas comment box flush with its pin's tip.
 *
 * The comment pin (see `CommentPinUtil`) is a 25px teardrop whose sharp corner
 * (`border-radius` 0 at bottom-left) is rotated -45deg to point straight down.
 * A 2px colored ring (`box-shadow: 0 0 0 2px`) wraps the pin; at the rotated
 * 90deg pointed corner that ring miters ~2.8px (2 * sqrt(2)) past the 25px box,
 * so the visible tip sits ~28px below the pin shape's top edge — not 27.
 *
 * The previous value of 27 (25px box + a flat 2px ring) ignored that miter and
 * left the box ~1px too high, overlapping the pin tip. Rounding the true tip
 * (25 + 2 * sqrt(2) = 27.83) up to 28 lands the comment box's top edge flush
 * with the tip: no overlap, no gap.
 */
export const PIN_TIP_OFFSET_PX = 28;

/**
 * Page-space Y for the comment box's top edge so it sits flush with the pin
 * tip. `pinY` is the pin (comment-avatar) shape's top Y in page space and
 * `zoomLevel` is the current editor zoom (the pin renders at `1 / zoomLevel`).
 */
export function getCommentBoxTopY(pinY: number, zoomLevel: number): number {
  return pinY + PIN_TIP_OFFSET_PX / zoomLevel;
}

/** Whether the box opens below the pin tip (default) or flips above it. */
export type CommentBoxPlacement = "below" | "above";

export interface CommentBoxPlacementInput {
  /** Pin (comment-avatar) shape's top Y in page space. */
  pinY: number;
  /** Current editor zoom; the pin renders at `1 / zoomLevel`. */
  zoomLevel: number;
  /** Comment box height, in the SAME (page) space as `pinY`/`minY`/`maxY`. */
  boxHeight: number;
  /** Top edge of the available bounds (viewport page bounds `minY`). */
  minY: number;
  /** Bottom edge of the available bounds (viewport page bounds `maxY`). */
  maxY: number;
}

export interface CommentBoxPlacementResult {
  /** Page-space Y for the comment box's top edge. */
  top: number;
  /** Which way the box ended up opening. */
  placement: CommentBoxPlacement;
}

/**
 * Decide where the comment box's top edge should sit so the box stays fully
 * on screen, given the pin position, the measured box height, and the visible
 * bounds (all in page space).
 *
 * The pin tip is the anchor in BOTH directions (see {@link getCommentBoxTopY}):
 * - "below" (default): the box top lands on the pin tip, opening downward —
 *   the existing, pin-tip-flush behavior, left untouched when there's room.
 * - "above" (flip): the box BOTTOM lands on the pin tip, opening upward,
 *   mirroring the `PIN_TIP_OFFSET_PX` tuck so it hugs the pin just as cleanly
 *   when there isn't room below.
 *
 * If flipping up would itself overflow the top edge (a box taller than the
 * space above the pin, e.g. a very tall thread in a short viewport), the top
 * is clamped to `minY` so the box stays within bounds and its own internal
 * scroll handles the remainder instead of clipping off-screen.
 */
export function getCommentBoxPlacement({
  pinY,
  zoomLevel,
  boxHeight,
  minY,
  maxY,
}: CommentBoxPlacementInput): CommentBoxPlacementResult {
  const pinTipY = getCommentBoxTopY(pinY, zoomLevel);

  // Room below: keep the box top flush with the pin tip, opening downward.
  if (pinTipY + boxHeight <= maxY) {
    return { top: pinTipY, placement: "below" };
  }

  // Flip above: anchor the box bottom at the pin tip, opening upward. Clamp to
  // `minY` when the box is too tall to fit above (internal scroll covers rest).
  const aboveTop = Math.max(minY, pinTipY - boxHeight);
  return { top: aboveTop, placement: "above" };
}

/** The box corner that sits against the pin tip (the one kept square). */
export type CommentBoxPinCorner = "tl" | "tr" | "bl" | "br";

/**
 * Pick the single box corner adjacent to the pin tip so the UI can leave that
 * corner square ("point to point" with the pin) and round the other three for
 * a connected look.
 *
 * The corner depends on BOTH the vertical flip and the horizontal shift:
 * - placement "below": the box opens downward, so the pin tip meets the box's
 *   TOP edge → square a top corner.
 * - placement "above": the box flipped up, so the tip meets the BOTTOM edge →
 *   square a bottom corner.
 * - `pinOnRight`: the box was nudged left to stay on screen, so the pin is at
 *   the box's RIGHT side → square the right corner; otherwise the box extends
 *   right from the pin and the pin is at the LEFT side → square the left corner.
 */
export function getCommentBoxPinCorner(
  placement: CommentBoxPlacement,
  pinOnRight: boolean,
): CommentBoxPinCorner {
  if (placement === "below") {
    return pinOnRight ? "tr" : "tl";
  }
  return pinOnRight ? "br" : "bl";
}
